import React, { useState } from 'react';
import { Send, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { schoolInfo, whatsappMessage } from '../data/mockData';

const AdmissionSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    parentName: '',
    childAge: '',
    phoneNumber: '',
    parentAddress: '',
    childGender: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (will be replaced with actual backend)
    setTimeout(() => {
      // Open WhatsApp with pre-filled message including form data
      const message = `Hi, I want to inquire about admissions.\n\nParent Name: ${formData.parentName}\nChild Age: ${formData.childAge}\nChild Gender: ${formData.childGender}\nPhone: ${formData.phoneNumber}\nAddress: ${formData.parentAddress}`;
      const whatsappUrl = `https://wa.me/${schoolInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
      
      // Store in localStorage temporarily (will be replaced with MongoDB)
      const submissions = JSON.parse(localStorage.getItem('admissionSubmissions') || '[]');
      submissions.push({
        ...formData,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('admissionSubmissions', JSON.stringify(submissions));

      toast({
        title: "Inquiry Submitted!",
        description: "Redirecting to WhatsApp to confirm your inquiry...",
      });

      setTimeout(() => {
        window.open(whatsappUrl, '_blank');
      }, 1000);

      // Reset form
      setFormData({ parentName: '', childAge: '', phoneNumber: '', parentAddress: '', childGender: '' });
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <section id="admissions" className="py-20 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-yellow-400 text-gray-900 px-4 py-2 rounded-full mb-6 shadow-lg animate-pulse">
              <Sparkles className="w-4 h-4" />
              <span className="font-semibold text-sm">Limited Seats Available</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Admissions Open for <span className="text-blue-600">2026-27</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Secure your child's future today. Join the Gyanodeepti family!
            </p>
            <div className="w-24 h-1 bg-yellow-400 mx-auto rounded-full mt-4" />
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Submit Your Inquiry
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="parentName" className="text-gray-700 font-medium mb-2 block">
                  Parent Name *
                </Label>
                <Input
                  id="parentName"
                  name="parentName"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.parentName}
                  onChange={handleChange}
                  required
                  className="h-12 text-base border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <Label htmlFor="childAge" className="text-gray-700 font-medium mb-2 block">
                  Child Age *
                </Label>
                <Input
                  id="childAge"
                  name="childAge"
                  type="text"
                  placeholder="e.g., 3 years or 3.5 years"
                  value={formData.childAge}
                  onChange={handleChange}
                  required
                  className="h-12 text-base border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <Label htmlFor="childGender" className="text-gray-700 font-medium mb-2 block">
                  Child Gender *
                </Label>
                <select
                  id="childGender"
                  name="childGender"
                  value={formData.childGender}
                  onChange={handleChange}
                  required
                  className="w-full h-12 text-base border border-gray-300 rounded-md px-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <Label htmlFor="phoneNumber" className="text-gray-700 font-medium mb-2 block">
                  Phone Number *
                </Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  pattern="[0-9+\s-]+"
                  className="h-12 text-base border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <Label htmlFor="parentAddress" className="text-gray-700 font-medium mb-2 block">
                  Parent Address *
                </Label>
                <textarea
                  id="parentAddress"
                  name="parentAddress"
                  rows="3"
                  placeholder="Enter your full address"
                  value={formData.parentAddress}
                  onChange={handleChange}
                  required
                  className="w-full text-base border border-gray-300 rounded-md px-3 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Inquiry
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              We'll redirect you to WhatsApp to confirm your inquiry
            </p>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-3xl font-bold text-blue-600">15+</p>
              <p className="text-sm text-gray-600">Years Experience</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-3xl font-bold text-blue-600">50+</p>
              <p className="text-sm text-gray-600">Happy Students</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-3xl font-bold text-blue-600">25+</p>
              <p className="text-sm text-gray-600">Expert Teachers</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-3xl font-bold text-blue-600">100%</p>
              <p className="text-sm text-gray-600">Safe Environment</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdmissionSection;
